import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import { Navbar } from '../components/custom/Navbar';
import { CartSheet } from '../components/custom/CartSheet';
import { FoodCard } from '../components/custom/FoodCard';
import { CategoryPill } from '../components/custom/CategoryPill';
import { CustomizationModal } from '../components/custom/CustomizationModal';
import { BundleBuilderModal } from '../components/custom/BundleBuilderModal';
import HomepageFAQ from '../components/custom/HomepageFAQ';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { menuAPI } from '../utils/api';
import { toast } from 'sonner';
import { SEO } from '../seo/SEO';
import * as meta from '../analytics/meta';
import { SITE_URL } from '../seo/businessConfig';

const HomePage = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [customizationItem, setCustomizationItem] = useState(null);
  const [bundleItem, setBundleItem] = useState(null);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    try {
      const [menuRes, categoriesRes] = await Promise.all([
        menuAPI.getMenu(),
        menuAPI.getCategories()
      ]);
      setMenuItems(menuRes.data);
      setCategories(['All', ...categoriesRes.data.categories]);
    } catch (error) {
      console.error('Failed to fetch menu:', error);
      toast.error('Failed to load menu');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (item) => {
    addToCart({
      item_id: item.id,
      name: item.name,
      price: item.finalPrice || item.price,
      image_url: item.image_url,
      customizations: item.customizations
    });
    toast.success(`${item.name} added to cart`);
  };

  const handleOpenCustomization = (item) => {
    // Meta Pixel `ViewContent` — fires whenever a customer opens a
    // product's customisation modal or the bundle builder. This is our
    // best signal that the customer "viewed" an item beyond scrolling.
    meta.viewContent({
      id: item.id,
      name: item.name,
      price: item.price,
      category: item.category,
      item_type: item.item_type,
    });
    if ((item.item_type || 'standard') === 'bundle') {
      setBundleItem(item);
      return;
    }
    setCustomizationItem(item);
  };

  const handleCloseCustomization = () => {
    setCustomizationItem(null);
  };

  const handleAddBundle = (payload) => {
    addToCart(payload);
    toast.success(`${payload.name} added to cart`);
    setBundleItem(null);
  };

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-background">
      {/* SEO — Restaurant + FAQPage JSON-LD are emitted from the
          prerender pipeline (scripts/postbuild_seo.cjs) so bots and
          AI answer engines see them without executing JS. We
          deliberately DON'T re-emit them from React to keep the final
          DOM at exactly ONE Restaurant and ONE FAQPage entity — that
          matches the AEO/GEO audit acceptance rule.

          Breadcrumbs + Menu schema are React-only because they depend
          on live cart / catalogue data. */}
      <SEO
        breadcrumbs={[{ name: 'Home', url: `${SITE_URL}/` }]}
        menuItems={menuItems}
        includeRestaurantSchema={false}
      />
      <Navbar />
      <CartSheet />

      {/* Visually hidden H1 for SEO — screen readers announce it, sighted
          users see the search-bar hero below. Ensures the homepage always
          has exactly one <h1> even after React hydrates away the
          prerender block. */}
      <h1 className="sr-only">Cravin Crrispy Takeaway &amp; Food Delivery in Forfar</h1>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-8">
        <div className="md:static sticky top-16 z-30 bg-background pb-4 md:pb-0">
          <div className="mb-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Search for dishes..."
                className="pl-10 h-12 rounded-full"
                value={searchQuery}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearchQuery(v);
                  // Debounced Meta Pixel `Search` event — the helper
                  // itself throttles to at most one call every 700ms
                  // and ignores queries shorter than 3 chars, so we
                  // can call it on every keystroke safely.
                  meta.search(v);
                }}
              />
            </div>
          </div>

          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex gap-3 pb-2">
              {categories.map(category => (
                <CategoryPill
                  key={category}
                  category={category}
                  active={selectedCategory === category}
                  onClick={() => setSelectedCategory(category)}
                />
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-gray-100 rounded-2xl h-40 animate-pulse" />
            ))}
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lg text-muted-foreground">No items found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-24 md:pb-8">
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <FoodCard 
                  item={item} 
                  onAddToCart={handleAddToCart}
                  onOpenCustomization={handleOpenCustomization}
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <CustomizationModal
        item={customizationItem}
        open={!!customizationItem}
        onClose={handleCloseCustomization}
        onAdd={handleAddToCart}
      />
      <BundleBuilderModal
        bundle={bundleItem}
        open={!!bundleItem}
        onClose={() => setBundleItem(null)}
        onAdd={handleAddBundle}
      />

      {/* Homepage FAQ block — visible copy is the single source of truth
          for the FAQPage JSON-LD emitted by <SEO includeFaqSchema />.
          Both read from HOMEPAGE_FAQS in seo/businessConfig.js so the
          rendered text and the schema can never diverge. */}
      <HomepageFAQ />
    </div>
  );
};

export default HomePage;
